import React from 'react';
import { addEdge, Background, Controls, ReactFlow, useEdgesState, useNodesState } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import {
  createFlowNode,
  EDGE_TYPES,
  EXECUTION_NODE_TYPES,
  generateCreationContextFrom,
  getCreateNodeTypeFromHandleId,
  NODE_COMPONENT_TYPES,
  NODE_HANDLE_IDS,
  NODE_HANDLE_TYPES,
  NODE_TYPES,
} from '@containers/flow/nodes';
import { DocSelectionModalController } from '@containers/flow/DocSelectionModalController';
import { CsvDocSelectionModalController } from '@containers/flow/CsvDocSelectionModalController';
import ConnectionLine from '@containers/flow/nodes/components/ConnectionLine';
import { flowQueryDefaultEdgeOptions, generateFlowQueryConnectionLineStyle } from '@constants/flowQuery';
import { useColorTheme } from '@hooks';
import { logError, logger } from '@utils/usefulFuncs';
import { FlowWrapper } from './styles';
import { PlanStepHelpers } from '@classes/helpers/PlanStepHelpers';
import { OutputAggregationHelpers } from '@classes/helpers/OutputAggregationHelpers';
import { useDispatch } from 'react-redux';
import { showErrorBanner } from '@utils/application';
import { AdvancedSettingsModalController } from '@containers/flow/AdvancedSettingsModalController';
import { FlowAgentProvider } from '@/contexts/FlowAgentContext';
import { validateAllPlanStepNodes } from '@utils/agentScriptValidation';

let connectionStartSourceNode = null;

function getNewNodeOptimalPosition(nodes) {
  const lastNode = nodes[nodes.length - 1];
  const lastNodePosition = lastNode?.position || { x: 24, y: 24 };
  return {
    x: lastNodePosition.x - 200,
    y: lastNodePosition.y,
  };
}

function calcPositionFromConnectEvent(event, viewport) {
  const { clientX, clientY } = event;
  const xOffset = -40;
  const yOffset = 10;
  const canvasX = (clientX - viewport.x - xOffset) / viewport.zoom - 80;
  const canvasY = (clientY - viewport.y - yOffset) / viewport.zoom - 80;
  return { x: canvasX, y: canvasY };
}

export function AgentScript() {
  const dispatch = useDispatch();
  const colorTheme = useColorTheme();

  const nodesRef = React.useRef([]);
  const edgesRef = React.useRef([]);

  const [viewport, setViewport] = React.useState({ x: 0, y: 0, zoom: 1 });
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [docSelectionModalInstance, setDocSelectionModalInstance] = React.useState(null);
  const [csvDocSelectionModalInstance, setCsvDocSelectionModalInstance] = React.useState(null);
  const [advancedSettingsModalInstance, setAdvancedSettingsModalInstance] = React.useState(null);
  const [validationResults, setValidationResults] = React.useState({});
  const validationResultsRef = React.useRef({});
  const [isEditable, setIsEditable] = React.useState(true);

  // Keep refs up to date with current state
  React.useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;
  }, [nodes, edges]);

  React.useEffect(() => {
    if (docSelectionModalInstance) {
      const init = () => {
        const delay = 700;
        setTimeout(() => {
          const startNode = createFlowNode(NODE_TYPES.AgentScriptStartNode);
          startNode.position = { x: 24, y: 24 };
          startNode.deletable = false;
          onAddNode(startNode);
        }, delay);
      };
      init();
    }
  }, [docSelectionModalInstance]);

  React.useEffect(() => {
    // logger('node state', nodes, null);
  }, [nodes]);

  React.useEffect(() => {
    // logger('edge state', edges, null);
  }, [edges]);

  // Run validation whenever nodes change
  React.useEffect(() => {
    const runValidation = () => {
      const allValidations = validateAllPlanStepNodes(nodes);
      setValidationResults(allValidations);
      validationResultsRef.current = allValidations;
    };

    // Run validation with a small delay to batch multiple changes
    const timeoutId = setTimeout(runValidation, 100);
    return () => clearTimeout(timeoutId);
  }, [nodes]);

  // Update nodes with validation results
  React.useEffect(() => {
    setNodes((prevNodes) => {
      let hasChanges = false;
      const nextNodes = prevNodes.map((node) => {
        if (node.type === NODE_TYPES.PlanStepNode) {
          const nodeValidations = validationResults[node.id]?.validations || [];
          if (JSON.stringify(node.data.validations) !== JSON.stringify(nodeValidations)) {
            hasChanges = true;
            return {
              ...node,
              data: {
                ...node.data,
                validations: nodeValidations,
              },
            };
          }
        }
        return node;
      });
      return hasChanges ? nextNodes : prevNodes;
    });
  }, [validationResults]);

  const onOpenDocSelectionModal = (nodeId, value, onChange) => {
    docSelectionModalInstance.showModal(nodeId, value, onChange);
  };

  const onOpenCsvDocSelectionModal = (nodeId, currentDocs, onChange) => {
    csvDocSelectionModalInstance.showModal(nodeId, currentDocs, onChange);
  };

  const onOpenAdvancedSettingsModal = (nodeId) => {
    advancedSettingsModalInstance.showModal(nodeId);
  };

  const removeSourceEdges = (nodeId) => {
    setEdges((eds) => eds.filter((e) => e.source !== nodeId));
  };

  const attemptCreateEdgeForNode = (targetNode, sourceNodeId, handleParams) => {
    const { handleId, handleType } = handleParams;
    logger('attemptCreateEdgeForNode', { targetNode, sourceNodeId, handleParams });
    const newEdge = {
      id: `e-${sourceNodeId}-${targetNode.id}`,
    };

    if (
      [
        NODE_HANDLE_IDS.executionFlowTarget,
        NODE_HANDLE_IDS.executionFlowSource,
        NODE_HANDLE_IDS.executionFlowSourceTrue,
        NODE_HANDLE_IDS.executionFlowSourceFalse,
      ].includes(handleId)
    ) {
      if (handleId === NODE_HANDLE_IDS.executionFlowSource) {
        newEdge.source = sourceNodeId;
        newEdge.sourceHandle = NODE_HANDLE_IDS.executionFlowSource;

        newEdge.target = targetNode.id;
        newEdge.targetHandle = NODE_HANDLE_IDS.executionFlowTarget;
      }
      if (handleId === NODE_HANDLE_IDS.executionFlowTarget) {
        newEdge.target = sourceNodeId;
        newEdge.targetHandle = NODE_HANDLE_IDS.executionFlowSource;

        newEdge.source = targetNode.id;
        newEdge.sourceHandle = NODE_HANDLE_IDS.executionFlowTarget;
      }
      if (handleId === NODE_HANDLE_IDS.executionFlowSourceTrue) {
        newEdge.target = targetNode.id;
        newEdge.targetHandle = NODE_HANDLE_IDS.executionFlowTarget;

        newEdge.source = sourceNodeId;
        newEdge.sourceHandle = NODE_HANDLE_IDS.executionFlowSourceTrue;
      }
      if (handleId === NODE_HANDLE_IDS.executionFlowSourceFalse) {
        newEdge.target = targetNode.id;
        newEdge.targetHandle = NODE_HANDLE_IDS.executionFlowTarget;

        newEdge.source = sourceNodeId;
        newEdge.sourceHandle = NODE_HANDLE_IDS.executionFlowSourceFalse;
      }
    } else {
      newEdge.type = 'custom'; // for sdk when using custom handles

      const isPlanStepConditionalSource =
        handleId === NODE_HANDLE_IDS.planStepYes || handleId === NODE_HANDLE_IDS.planStepNo;

      if (handleType === NODE_HANDLE_TYPES.target) {
        newEdge.target = sourceNodeId;
        newEdge.source = targetNode.id;
        newEdge.targetHandle = handleId;
        newEdge.sourceHandle = handleId;
      }
      if (handleType === NODE_HANDLE_TYPES.source) {
        newEdge.source = sourceNodeId;
        newEdge.target = targetNode.id;
        newEdge.sourceHandle = handleId;
        // Plan step yes/no source handles connect to the plan step's single `plan-step` target handle.
        newEdge.targetHandle = isPlanStepConditionalSource ? NODE_HANDLE_IDS.planStep : handleId;
      }

      const isExecutionTypeNodeTarget = EXECUTION_NODE_TYPES.includes(targetNode.type);
      if (isExecutionTypeNodeTarget) {
        const secondaryNodeEdge = { id: `se-${sourceNodeId}-${targetNode.id}` };
        secondaryNodeEdge.source = sourceNodeId;
        secondaryNodeEdge.sourceHandle = NODE_HANDLE_IDS.executionFlowSource;

        secondaryNodeEdge.target = targetNode.id;
        secondaryNodeEdge.targetHandle = NODE_HANDLE_IDS.executionFlowTarget;

        logger('creating secondary edge', secondaryNodeEdge);
        setEdges((eds) => addEdge(secondaryNodeEdge, eds));
      }
    }

    logger('creating edge', newEdge);
    setEdges((eds) => addEdge(newEdge, eds));
  };

  const replaceNode = (sourceNodeId, currentNodeId, type, params) => {
    let nodeToReplace = null;
    setNodes((prevState) => {
      const updatedNodes = prevState.filter((node) => node.id !== currentNodeId);
      nodeToReplace = prevState.find((node) => node.id === currentNodeId);
      return updatedNodes;
    });

    const newNode = createFlowNode(type);
    newNode.position = nodeToReplace.position;
    onAddNode(newNode);

    logger('replacing node', newNode, {
      sourceNodeId,
      currentNodeId,
      type,
      params,
      replacedNode: nodeToReplace,
    });

    attemptCreateEdgeForNode(newNode, sourceNodeId, params);
  };

  const validateEdge = () => true;

  const onMove = React.useCallback((event, { x, y, zoom }) => {
    setViewport({ x, y, zoom });
  }, []);

  const onShowError = (message) => {
    showErrorBanner(message, null, dispatch);
  };

  const onConnect = React.useCallback((params) => {
    if (validateEdge) {
      return setEdges((eds) => addEdge(params, eds));
    } else {
      onShowError('Cannot connect these nodes');
    }
  }, []);

  const onRemoveHandleEdges = (nodeId, handleId, handleType) => {
    setEdges((prevEdges) =>
      prevEdges.filter((e) => {
        if (handleType === 'source') {
          return !(e.source === nodeId && e.sourceHandle === handleId);
        } else if (handleType === 'target') {
          return !(e.target === nodeId && e.targetHandle === handleId);
        }
        return true;
      })
    );
  };

  const onAddNode = (node) => {
    const actions = {
      onRemove: onRemoveNode,
      onRemoveAllLinks,
      onDuplicateNode,
      updateNodeData,
      updateNodePlanStepData,
      removeNodePlanStepKeyName,
      createNodesAndEdgesForLoadedAgentScript,
      setScriptIsEditable: setIsEditable,
      openDocSelectionModal: onOpenDocSelectionModal,
      openCsvDocSelectionModal: onOpenCsvDocSelectionModal,
      openAdvancedSettingsModal: onOpenAdvancedSettingsModal,
      removeSourceEdges,
    };
    setNodes((prevNodes) => {
      if (!node.position) {
        const { x, y } = getNewNodeOptimalPosition(prevNodes);
        node.position = { x, y };
      }

      if (node.type === NODE_TYPES.AgentScriptStartNode) {
        node.data.nodesRef = nodesRef;
        node.data.edgesRef = edgesRef;
      }

      if (node.type === NODE_TYPES.AgentScriptCreateNode) {
        node.data.actions.onRemove = onRemoveNode;
        node.data.actions.replaceNode = (sourceNodeId, currentNodeId, type, params) =>
          replaceNode(sourceNodeId, currentNodeId, type, params);
      } else {
        node.data.actions = actions;
      }

      if (node.type === NODE_TYPES.PlanStepNode) {
        node.data.actions.removeHandleEdges = (nodeId, handleId, handleType) =>
          onRemoveHandleEdges(nodeId, handleId, handleType);
        if (!node.data.planStepData) {
          node.data.planStepData = { step_id: '', step_type: '' };
        }
      }

      if (node.type === NODE_TYPES.AgentScriptQueryNode) {
        if (!node.data.selectedDocs) {
          node.data.selectedDocs = [];
        }
      }

      if (node.type === NODE_TYPES.OutputAggregationNode) {
        if (!node.data.stepOutputPriorityOrder) {
          node.data.stepOutputPriorityOrder = null;
        }
      }

      return [...prevNodes, node];
    });
  };

  const onConnectStart = React.useCallback(
    (event, params) => {
      logger('onConnectStart', { event, params });
      const node = nodes.find((n) => n.id === params.nodeId);
      connectionStartSourceNode = node;
      const { handleId, handleType } = params;
      const handleParams = {
        handleId,
        handleType,
      };
      connectionStartSourceNode.handleParams = handleParams;
    },
    [nodes]
  );

  const onConnectEnd = (event) => {
    const { target } = event;

    const connectedToNodeId = target?.getAttribute('data-nodeid');
    const connectedToNode = nodes.find((node) => node.id === connectedToNodeId); // to find the node that was connected to

    logger('connect end', { event, target, connectedToNode, connectionStartSourceNode });

    if (connectedToNode) {
      return;
    }

    const isTargetBackground = target?.classList.contains('react-flow__pane');
    if (isTargetBackground) {
      const handleParams = {
        handleId: connectionStartSourceNode.handleParams.handleId,
        handleType: connectionStartSourceNode.handleParams.handleType,
      };
      const nodeType = getCreateNodeTypeFromHandleId(handleParams.handleId, handleParams.handleType);
      const hasAutoNodeType = nodeType !== null;
      if (hasAutoNodeType) {
        const newNode = createFlowNode(nodeType);
        newNode.position = calcPositionFromConnectEvent(event, viewport);
        onAddNode(newNode);
        attemptCreateEdgeForNode(newNode, connectionStartSourceNode.id, handleParams);

        connectionStartSourceNode = null;
        return true;
      }
      if (!connectedToNode && !!connectionStartSourceNode) {
        const creationContext = generateCreationContextFrom(connectionStartSourceNode);
        if (creationContext) {
          const newNode = createFlowNode(NODE_TYPES.AgentScriptCreateNode);
          newNode.data.sourceNodeId = connectionStartSourceNode.id;
          newNode.data.creationContext = creationContext;
          newNode.data.connectionStartSourceNode = connectionStartSourceNode;
          newNode.position = calcPositionFromConnectEvent(event, viewport);
          onAddNode(newNode);

          connectionStartSourceNode = null;
          return true;
        }
      }
    }
  };

  const onDocSelectionModalInit = (ref) => {
    setDocSelectionModalInstance(ref);
  };

  const onCsvDocSelectionModalInit = (ref) => {
    setCsvDocSelectionModalInstance(ref);
  };

  const onAdvancedSettingsModalInit = (ref) => {
    setAdvancedSettingsModalInstance(ref);
  };

  const updateNodeData = (id, data) => {
    setNodes((prevNodes) => {
      const index = prevNodes.findIndex((f) => f.id === id);
      const nextState = [...prevNodes];
      nextState[index] = {
        ...nextState[index],
        data: {
          ...nextState[index].data,
          ...data,
        },
      };
      return nextState;
    });
  };

  const createNodesAndEdgesForLoadedAgentScript = (startNodeId, agentScript) => {
    const { json } = agentScript;
    logger('createNodesAndEdgesForLoadedAgentScript', { agentScript });

    const { plan_steps: planSteps, final_output_aggregation: finalOutputAggregation } = json;

    const nodes = [];
    (planSteps || []).forEach((planStepData, i) => {
      const data = { planStepData };
      const node = PlanStepHelpers.generateNode(data, i);
      nodes.push(node);
      onAddNode(node);
    });
    const planStepNodes = nodes.filter((node) => node.data.type === NODE_TYPES.PlanStepNode);
    const planStepEdges = PlanStepHelpers.generateEdges(planStepNodes, startNodeId);

    let stepOutputPriorityOrder = [];
    if (finalOutputAggregation) {
      stepOutputPriorityOrder = finalOutputAggregation?.step_output_priority_order || [];
    }
    stepOutputPriorityOrder.forEach((elData, i) => {
      const data = { stepOutputPriorityOrder: elData };
      const node = OutputAggregationHelpers.generateNode(data, i);
      nodes.push(node);
      onAddNode(node);
    });
    const outputAggregationNodes = nodes.filter((node) => node.data.type === NODE_TYPES.OutputAggregationNode);
    const outputAggregationEdges = OutputAggregationHelpers.generateEdges(outputAggregationNodes, startNodeId);
    setEdges([...planStepEdges, ...outputAggregationEdges]);
  };

  const updateNodePlanStepData = (id, data) => {
    setNodes((prevNodes) => {
      const index = prevNodes.findIndex((f) => f.id === id);
      const nextState = [...prevNodes];
      nextState[index] = {
        ...nextState[index],
        data: {
          ...nextState[index].data,
          planStepData: {
            ...(nextState[index].data.planStepData || {}),
            ...data,
          },
        },
      };
      return nextState;
    });
  };

  const removeNodePlanStepKeyName = (id, keyName) => {
    setNodes((prevNodes) => {
      const index = prevNodes.findIndex((f) => f.id === id);
      const nextState = [...prevNodes];
      const planStepData = {
        ...(nextState[index].data.planStepData || {}),
      };
      delete planStepData[keyName];
      nextState[index] = {
        ...nextState[index],
        data: {
          ...nextState[index].data,
          planStepData,
        },
      };
      return nextState;
    });
  };

  const onRemoveAllLinks = (nodeId) => {
    setEdges((prevEdges) =>
      prevEdges.filter((e) => {
        if (e.target === nodeId) return false;
        if (e.source === nodeId) return false;
        return true;
      })
    );
  };

  const onDuplicateNode = (nodeId) => {
    const node = nodesRef.current.find((n) => n.id === nodeId);
    if (!node) {
      logError('Node not found', nodeId);
      return;
    }

    const newNode = createFlowNode(node.type);
    newNode.data = { ...node.data };
    newNode.data.planStepData = { ...node.data.planStepData, step_id: `${node.data.planStepData.step_id}-copy` };

    newNode.position = {};
    newNode.position.y = node.position.y + 28;
    newNode.position.x = node.position.x + 28;
    newNode.selected = true; // Select the new node
    onAddNode(newNode);
  };

  const onRemoveNode = (nodeId) => {
    setNodes((prevNodes) => {
      const node = prevNodes.find((n) => n.id === nodeId);
      if (node?.type === NODE_TYPES.AgentScriptStartNode) return prevNodes;
      return prevNodes.filter((f) => f.id !== nodeId);
    });
  };

  return (
    <>
      <DocSelectionModalController onInit={onDocSelectionModalInit} nodes={nodes} updateNodeData={updateNodeData} />
      <CsvDocSelectionModalController onInit={onCsvDocSelectionModalInit} />
      <AdvancedSettingsModalController
        onInit={onAdvancedSettingsModalInit}
        nodes={nodes}
        updateNodeData={updateNodeData}
      />

      <FlowWrapper className={isEditable ? undefined : 'flow-read-only'}>
        <FlowAgentProvider nodes={nodes} isEditable={isEditable}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_COMPONENT_TYPES}
            edgeTypes={EDGE_TYPES}
            defaultEdgeOptions={flowQueryDefaultEdgeOptions}
            connectionLineStyle={generateFlowQueryConnectionLineStyle(colorTheme)}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={isEditable ? onConnect : undefined}
            onConnectStart={isEditable ? onConnectStart : undefined}
            onConnectEnd={isEditable ? onConnectEnd : undefined}
            connectionLineComponent={ConnectionLine}
            onMove={onMove}
            zoomOnScroll={false}
            autoPanOnNodeFocus={true}
            preventScrolling={false}
            elevateEdgesOnSelect={true}
            nodesDraggable={isEditable}
            nodesConnectable={isEditable}
            edgesReconnectable={isEditable}
            deleteKeyCode={isEditable ? ['Backspace', 'Delete'] : null}
          >
            <Controls />
            <Background variant="dots" gap={12} size={1} />
          </ReactFlow>
        </FlowAgentProvider>
      </FlowWrapper>
    </>
  );
}
